import BenchlingConfig from 'BenchlingConfig';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import TemplateCollectionDAO, {
  CreateTemplateParams,
} from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import {TEMPLATE_COLLECTIBLE_TYPES} from 'notebook/template-collections/TemplateCollectionTypes';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {FilterKey, keyToEmptyFilter, SearchParams} from 'search/SearchConstants';
import {getAppliedSortsFromUserPreference} from 'search/SearchPreferences';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';

function templateCollectionSearchParams(templateCollectionId: string): SearchParams {
  return {
    filters: [
      {...keyToEmptyFilter(FilterKey.TEMPLATE_COLLECTION), value: templateCollectionId},
      {
        ...keyToEmptyFilter(FilterKey.TYPES),
        value: TEMPLATE_COLLECTIBLE_TYPES.filter((t) => t !== ModelTypeConstants.ANALYSIS_TEMPLATE),
      },
    ],
    offset: 0,
    limit: getSelectedPageSize(),
    query: '',
    sorts: getAppliedSortsFromUserPreference(Section.TEMPLATE_COLLECTIONS),
  };
}

function openCollection<T extends {id: string; url: string}>(collection: T): void {
  const {id: collectionId, url} = collection;
  if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
    // Ensure that we don't accidentally show a previously loaded saved search as still active.
    getStore().dispatch(SearchUIReduxActions.setSelectedSavedSearch(undefined, Section.TEMPLATE_COLLECTIONS));
    getStore().dispatch(
      SidepanelReduxActions.openListing(
        Section.TEMPLATE_COLLECTIONS,
        templateCollectionSearchParams(collectionId)
      )
    );
  } else {
    navigateToURL(url);
  }
}

function openCollectible<T extends {id: string; editUrl: string}>(collectible: T): void {
  const {id, editUrl} = collectible;
  if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
    const FileBrowserActions = require('file-browser/FileBrowserActions').default;
    FileBrowserActions.openFile(id, {folderItem: collectible});
  } else {
    navigateToURL(editUrl);
  }
}

// TODO(T3849): Make action fire-and-forget
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function updateWithXHR(templateCollectionId: string, data: CreateTemplateParams): JQueryPromise<any> {
  AppDispatcher.dispatch({
    actionType: EventConstants.TEMPLATE_COLLECTION_UPDATE,
    collectionId: templateCollectionId,
    data,
  });
  return TemplateCollectionDAO.update(templateCollectionId, data)
    .done((templateCollectionResponse) => {
      writeFragmentsToCache(getFragmentWrapper('TemplateCollectionSummary'), [
        {
          __typename: 'TemplateCollectionNode',
          ...templateCollectionResponse,
        },
      ]);
    })
    .fail(notifications.notifyError);
}

// TODO(T3849): Make action fire-and-forget
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deleteWithXHR(templateCollectionId: string): JQueryPromise<any> {
  AppDispatcher.dispatch({
    actionType: EventConstants.TEMPLATE_COLLECTION_DELETE,
    templateCollectionId,
  });
  return TemplateCollectionDAO.delete(templateCollectionId)
    .done((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.TEMPLATE_COLLECTION_DELETE_COMPLETED,
        collectionId: templateCollectionId,
        collection: response,
      });
    })
    .then(() => {
      navigateToURL(TemplateCollectionDAO.collectionsURL());
    })
    .fail(notifications.notifyError);
}

export default {
  updateWithXHR,
  deleteWithXHR,
  openCollection,
  openCollectible,
};
